<template>
  <div class="screen-container">
    <v-layout column>
      <v-flex class="title-screen" xs12><span>{{ $adminT('events.liveHeading', 'Live Events') }}</span></v-flex>
      <div>
        <div v-if="eventCreatedMsg" class="event-created-wrapper">
          <div class="event-created">
            <img src="/assets/done.svg" :alt="$adminT('events.createdAlt', 'Event created')" class="event-created-img">
            <p class="event-created-msg">{{ $adminT('events.createdNotice', 'Event successfully created') }}</p>
          </div>
          <div class="close-wrapper" @click="closeMsg">
            <p class="close-msg">{{ $adminT('events.close', 'Close') }}</p>
            <v-icon color="#55A35D" class="close-icon">close</v-icon>
          </div>
        </div>
        <div class="create-search-container">
          <main-button
            v-if="eventQuota > 0"
            :disabled="!user"
            style="max-width: 244px; margin-bottom: 1rem"
            @click="onCreateEventClick"
          >
            <v-icon style="margin-right: 0.5rem">add</v-icon>
            {{ $adminT('events.createEvent', 'Create event') }}
          </main-button>
          <text-field
            v-if="searchVisible"
            :value="searchInput"
            :label="$adminT('events.searchLive', 'Search Your Live events')"
            append-icon="search"
            style="max-width: 24.063rem"
            @input="onSearchChange"
          />
        </div>
        <div v-if="loadError" class="events-error" role="alert">
          <span>{{ $adminT('events.listError', 'Events could not be loaded. Please try again.') }}</span>
          <v-btn flat small color="primary" @click="reloadEvents">{{ $adminT('events.retry', 'Retry') }}</v-btn>
        </div>
        <div v-else-if="isInitialLoading" class="event-container" aria-busy="true" :aria-label="$adminT('events.loadingLive', 'Loading live events')">
          <event-card-skeleton v-for="index in 3" :key="`initial-skeleton-${index}`" />
        </div>
        <div v-else-if="!hasEvents && searchInput" class="empty-search-result">{{ $adminT('events.noLiveMatch', 'No live events match your search.') }}</div>
        <div v-else class="event-container">
          <event-card
            v-for="event in events"
            :key="event.id"
            :event="event"
            :organization-type="organizationType"
            :organization-slug="organizationSlug"
            :sales-stats-loading="isSalesStatsLoading(event)"
            :sales-stats-unavailable="isSalesStatsUnavailable(event)"
          />
          <div v-if="hasMore && !isRefreshing" ref="loadMoreTrigger" class="load-more-indicator" aria-live="polite">
            <template v-if="isLoadingMore">
              <event-card-skeleton v-for="index in 2" :key="`more-skeleton-${index}`" />
              <span>{{ $adminT('events.loadingMore', 'Loading more events…') }}</span>
            </template>
            <template v-else>
              <button type="button" class="load-more-button" @click="fetchEvents()">
                <v-icon small>expand_more</v-icon>
                {{ $adminT('events.loadMore', 'Load 10 more events') }}
              </button>
            </template>
          </div>
          <div v-else-if="hasLoadedMore && hasEvents && !isRefreshing" class="end-of-events" aria-live="polite">
            <v-icon small color="#55A35D">check_circle</v-icon>
            <span>{{ searchInput ? $adminT('events.allMatches', 'You have seen all matching events.') : $adminT('events.allLive', 'You have seen all live events.') }}</span>
          </div>
        </div>
      </div>
      <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
    </v-layout>
    <demo-modal v-if="demoModal" :user="user" @close="demoModal = false" />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { GET_ORGANIZATION_EVENTS_PAGE, GET_ORGANIZATION_EVENT_SALES_STATS } from '@/graphql/event.graphql'
import { OrganizationType } from '@/types'
import { max, min, round, sumBy } from 'lodash'
import DemoModal from '@/components/organizations/DemoModal.vue'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import eventCard from '../../events/event-card.vue'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import { eventListCacheKey, getCachedEventListValue, setCachedEventListValue } from './events-list-cache'

const PAGE_SIZE = 10
const CACHE_TTL = 5 * 60 * 1000
const eventCardSkeleton = {
  template: `
    <div class="event-card-skeleton" aria-hidden="true">
      <vue-skeleton-loader rounded radius="8" width="132px" height="88px" animation="fade" />
      <div class="event-card-skeleton__content">
        <vue-skeleton-loader rounded radius="4" width="42%" height="14px" animation="fade" />
        <vue-skeleton-loader rounded radius="4" width="72%" height="23px" animation="fade" />
        <vue-skeleton-loader rounded radius="4" width="46%" height="17px" animation="fade" />
      </div>
    </div>
  `,
}

export default {
  mixins: [OrganizationLocalization],
  name: 'organization-live-events-list-container',
  components: { DemoModal, eventCard, eventCardSkeleton, simpleSnackbar },
  props: {
    organizationSlug: String,
    eventCreated: { type: Boolean, default: false },
  },
  data() {
    return {
      organizationType: null,
      OrganizationType,
      snackbar: null,
      events: [],
      eventQuota: 0,
      demoModal: false,
      eventCreatedMsg: this.eventCreated,
      searchInput: '',
      searchVisible: false,
      searchTimeout: null,
      page: 1,
      hasMore: false,
      hasLoadedMore: false,
      isInitialLoading: true,
      hasLoadedInitialEvents: false,
      isLoadingMore: false,
      isRefreshing: false,
      loadError: false,
      requestId: 0,
      salesStatsLoading: {},
      salesStatsUnavailable: {},
      loadMoreObserver: null,
      scrollTicking: false,
      user: undefined,
    }
  },
  computed: {
    hasEvents() {
      return this.events.length > 0
    },
  },
  mounted() {
    this.fetchEvents({ reset: true })
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    document.addEventListener('scroll', this.handleScroll, true)
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
    if (this.loadMoreObserver) this.loadMoreObserver.disconnect()
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('scroll', this.handleScroll, true)
  },
  methods: {
    closeMsg() {
      this.eventCreatedMsg = false
    },
    onCreateEventClick() {
      if (!this.user) return
      if (!this.user.isVerifiedByCoreEvent && this.events.length >= 1) this.demoModal = true
      else this.handleGoTo()
    },
    handleGoTo() {
      if (this.organizationType === this.OrganizationType.REGULAR) this.goTo(this.routes.organization.organizationAddEvent)
      else if (this.organizationType === this.OrganizationType.THEATER) this.goTo(this.routes.theaterOrganization.theaterOrganizationAddEvent)
      else if (this.organizationType === this.OrganizationType.SPORT_ORGANIZATION) this.goTo(this.routes.sportOrganization.sportOrganizationAddEvent)
    },
    getQueryVariables(page = this.page) {
      return {
        organizationSlug: this.organizationSlug,
        filter: 'live',
        page,
        perPage: PAGE_SIZE,
        searchTerm: this.searchInput.trim() || null,
      }
    },
    async fetchEvents({ reset = false } = {}) {
      if (this.isLoadingMore || (!reset && (!this.hasMore || this.isInitialLoading || this.isRefreshing))) return
      const page = reset ? 1 : this.page + 1
      const requestId = ++this.requestId
      this.loadError = false
      if (reset) {
        this.isInitialLoading = !this.hasLoadedInitialEvents
        this.isRefreshing = !this.isInitialLoading
        this.page = 1
        this.hasLoadedMore = false
      } else this.isLoadingMore = true

      try {
        const variables = this.getQueryVariables(page)
        const cacheKey = eventListCacheKey('live-events', variables)
        let result = getCachedEventListValue(cacheKey)
        if (!result) {
          const { data } = await this.$apollo.query({
            query: GET_ORGANIZATION_EVENTS_PAGE,
            variables,
            fetchPolicy: 'network-only',
          })
          result = data.getOrganizationEventsPage
          setCachedEventListValue(cacheKey, result, CACHE_TTL)
        }
        if (requestId !== this.requestId) return
        this.hasLoadedInitialEvents = true
        if (result.items.length || this.searchInput) this.searchVisible = true
        const newEvents = this.mapEventCards(result.items, result.organizationType)
        this.organizationType = result.organizationType
        this.eventQuota = result.eventQuota
        this.events = reset ? newEvents : [...this.events, ...newEvents]
        this.page = page
        this.hasMore = result.hasMore
        if (!reset) this.hasLoadedMore = true
        this.loadSalesStats(result.items, page, requestId)
        this.$nextTick(() => {
          this.observeLoadMore()
          this.handleScroll()
        })
      } catch (error) {
        if (requestId === this.requestId) this.loadError = true
      } finally {
        if (requestId === this.requestId) {
          this.isInitialLoading = false
          this.isLoadingMore = false
          this.isRefreshing = false
        }
      }
    },
    mapEventCards(events, organizationType) {
      if (organizationType !== this.OrganizationType.THEATER) {
        return events.map(event => ({ ...event, salesStatsIds: [event.id] }))
      }
      const cardsById = events.reduce((cards, event) => {
        const id = event.eventTemplate ? `template-${event.eventTemplate.id}` : `event-${event.id}`
        if (!cards[id]) cards[id] = []
        cards[id].push(event)

        return cards
      }, {})

      return Object.values(cardsById)
        .map(performances => {
          const first = performances[0]
          if (!first.eventTemplate) return { ...first, salesStatsIds: [first.id] }

          return {
            ...first.eventTemplate,
            from: min(performances.map(performance => performance.from)),
            to: max(performances.map(performance => performance.to)),
            cover: first.cover,
            location: first.location,
            extraModules: first.extraModules,
            salesStatsIds: performances.map(performance => performance.id),
          }
        })
        .sort((event1, event2) => new Date(event1.from) - new Date(event2.from))
    },
    async loadSalesStats(events, page, requestId) {
      const variables = this.getQueryVariables(page)
      const cacheKey = eventListCacheKey('live-sales-stats', variables)
      const cachedStats = getCachedEventListValue(cacheKey)
      if (cachedStats) {
        this.applySalesStats(cachedStats)
        return
      }
      const ids = events.filter(event => event.extraModules && event.extraModules.ticketing).map(event => event.id)
      ids.forEach(id => this.$set(this.salesStatsLoading, id, true))
      ids.forEach(id => this.$set(this.salesStatsUnavailable, id, false))
      if (!ids.length) return
      try {
        const { data } = await this.$apollo.query({
          query: GET_ORGANIZATION_EVENT_SALES_STATS,
          variables,
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.requestId) return
        const stats = data.getOrganizationEventsPage.items
        setCachedEventListValue(cacheKey, stats, CACHE_TTL)
        this.applySalesStats(stats)
      } catch (error) {
        if (requestId === this.requestId) ids.forEach(id => this.$set(this.salesStatsUnavailable, id, true))
      } finally {
        if (requestId === this.requestId) ids.forEach(id => this.$set(this.salesStatsLoading, id, false))
      }
    },
    applySalesStats(stats) {
      const statsById = stats.reduce((values, event) => {
          values[event.id] = event

          return values
        }, {})
      this.events.forEach(event => {
        const eventStats = event.salesStatsIds.map(id => statsById[id]).filter(Boolean)
        if (!eventStats.length) return
        this.$set(event, 'totalTicketsSold', sumBy(eventStats, 'totalTicketsSold'))
        this.$set(event, 'totalNumberOfTickets', sumBy(eventStats, 'totalNumberOfTickets'))
        this.$set(event, 'totalEarnings', round(sumBy(eventStats, 'totalEarnings'), 2))
      })
    },
    isSalesStatsLoading(event) {
      return event.salesStatsIds.some(id => this.salesStatsLoading[id])
    },
    isSalesStatsUnavailable(event) {
      return event.salesStatsIds.some(id => this.salesStatsUnavailable[id])
    },
    onSearchChange(input) {
      this.searchInput = input || ''
      this.searchVisible = true
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.fetchEvents({ reset: true }), 300)
    },
    reloadEvents() {
      this.fetchEvents({ reset: true })
    },
    observeLoadMore() {
      if (!this.hasMore || !this.$refs.loadMoreTrigger || typeof IntersectionObserver === 'undefined') return
      if (this.loadMoreObserver) this.loadMoreObserver.disconnect()
      this.loadMoreObserver = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) this.fetchEvents()
        },
        { rootMargin: '240px 0px' },
      )
      this.loadMoreObserver.observe(this.$refs.loadMoreTrigger)
    },
    handleScroll() {
      if (this.scrollTicking || !this.hasMore || this.isLoadingMore || this.isRefreshing) return
      this.scrollTicking = true
      window.requestAnimationFrame(() => {
        this.scrollTicking = false
        const trigger = this.$refs.loadMoreTrigger
        if (trigger && trigger.getBoundingClientRect().top <= window.innerHeight + 200) this.fetchEvents()
      })
    },
  },
  apollo: {
    me: {
      query: GET_LOGGED_USER,
      result({ data, loading }) {
        if (!loading && data) this.user = data.me
      },
    },
  },
}
</script>

<style scoped>
.event-container { display: flex; flex-direction: column; max-width: 42.75rem; gap: 1rem; }
.create-search-container { display: flex; justify-content: space-between; flex-wrap: wrap; max-width: 42.75rem; margin-bottom: 1.5rem; }
.event-card-skeleton { min-height: 7.5rem; display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #e8edf0; border-radius: 0.75rem; background: #fff; }
.event-card-skeleton__content { display: flex; flex: 1; flex-direction: column; gap: 0.625rem; }
.load-more-indicator { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; color: #6d7780; font-size: 0.875rem; padding: 0.75rem 0 1.5rem; }
.load-more-button { display: inline-flex; align-items: center; gap: 0.375rem; border: 0; border-radius: 1.25rem; background: #eaf4fb; color: #0277bd; cursor: pointer; font: inherit; font-weight: 600; padding: 0.5rem 0.875rem; }
.load-more-button:hover, .load-more-button:focus { background: #dceef9; outline: none; }
.end-of-events { display: flex; align-items: center; justify-content: center; gap: 0.5rem; color: #6d7780; font-size: 0.875rem; padding: 1rem 0 1.5rem; }
.events-error { display: flex; align-items: center; gap: 0.5rem; color: #db384c; margin-bottom: 1rem; }
.empty-search-result { color: #6d7780; }
.event-created-wrapper { background: #eef6ef; border-radius: 0.75rem; color: #55a35d; display: flex; justify-content: space-between; align-items: center; max-width: 39.75rem; margin-bottom: 2.5rem; }
.event-created-msg { margin-left: 1rem; font-size: 1.5rem; }
.event-created { margin-left: 0.75rem; font-family: 'Raleway'; font-style: normal; font-weight: 600; color: #55a35d; display: flex; padding-top: 0.75rem; }
.close-msg { font-family: 'Raleway'; font-style: normal; font-weight: 600; font-size: 1.25rem; cursor: pointer; color: #55a35d; margin-top: 0.95rem; margin-right: 0.5rem; }
@media screen and (max-width: 500px) { .event-container { gap: 0; } }
@media screen and (max-width: 560px) { .event-created-msg { font-size: 1rem !important; margin-top: 0.5rem; } .close-msg { display: none; } .close-icon { margin-top: 5px; } .event-container { max-width: 100%; } }
</style>
